import { Modal, Button, Form, Alert, Image } from 'react-bootstrap';
import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';

import InfoTooltip from '@/components/common/InfoTooltip';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
    validateImageFile,
    ACCEPTED_FILE_EXTENSIONS,
} from '@/shared/utils/imageValidation';

const CATEGORIES = [
    { value: 'environment', label: 'Environment' },
    { value: 'energy', label: 'Energy' },
    { value: 'food', label: 'Food' },
    { value: 'water', label: 'Water' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'waste', label: 'Waste' },
    { value: 'general', label: 'General' },
];

const CreateCommunityModal = ({ show, onHide, onSubmit }) => {
    const { isAdmin } = useAuth();

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('');
    const [heroImage, setHeroImage] = useState(null);
    const [iconImage, setIconImage] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    // When set, the modal shows a "request submitted" confirmation
    // instead of the form. Only used for the non-admin request flow.
    const [submittedRequestName, setSubmittedRequestName] = useState(null);

    const heroInputRef = useRef(null);
    const iconInputRef = useRef(null);

    const generateSlug = (text) => {
        return text
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/[\s-]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const validateForm = () => {
        const newErrors = {};

        if (!name.trim()) {
            newErrors.name = 'Community name is required.';
        } else if (name.length > 100) {
            newErrors.name = 'Name must be 100 characters or less.';
        } else if (!generateSlug(name)) {
            newErrors.name = 'Name must contain at least one letter or number.';
        }

        if (!description.trim()) {
            newErrors.description = 'Description is required.';
        } else if (description.length > 2000) {
            newErrors.description =
                'Description must be 2000 characters or less.';
        }

        if (!category) {
            newErrors.category = 'Please select a category.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleImageSelect = (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        const validationError = validateImageFile(file);
        if (validationError) {
            setErrors({ ...errors, [type]: validationError });
            return;
        }

        // Clear any previous errors
        setErrors({ ...errors, [type]: null });

        // Create base64 data URL
        const reader = new FileReader();
        reader.onloadend = () => {
            if (type === 'hero') {
                setHeroImage(reader.result);
            } else {
                setIconImage(reader.result);
            }
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveImage = (type) => {
        if (type === 'hero') {
            setHeroImage(null);
            if (heroInputRef.current) heroInputRef.current.value = '';
        } else {
            setIconImage(null);
            if (iconInputRef.current) iconInputRef.current.value = '';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setIsSubmitting(true);

        const submittedName = name.trim();

        try {
            await onSubmit({
                name: submittedName,
                slug: generateSlug(name),
                description: description.trim(),
                category,
                heroImage: heroImage || '',
                icon: iconImage || '',
            });

            if (isAdmin) {
                // Admin flow: parent navigates to the new community.
                resetForm();
                onHide();
            } else {
                // Non-admin flow: surface the request-submitted confirmation
                // in place of the form and keep the modal open until dismissed.
                resetForm();
                setSubmittedRequestName(submittedName);
            }
        } catch (err) {
            setErrors({
                ...errors,
                submit:
                    err.response?.data?.message ||
                    (isAdmin
                        ? 'Failed to create community. Please try again.'
                        : 'Failed to submit request. Please try again.'),
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setName('');
        setDescription('');
        setCategory('');
        setHeroImage(null);
        setIconImage(null);
        setErrors({});
    };

    const handleClose = () => {
        resetForm();
        setSubmittedRequestName(null);
        onHide();
    };

    const titleText = isAdmin ? 'Create a Community' : 'Request a Community';
    const submitIdleText = isAdmin ? 'Create Community' : 'Submit Request';
    const submitBusyText = isAdmin ? 'Creating...' : 'Submitting...';

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            size="lg"
            aria-labelledby="create-community-modal-title"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title
                    id="create-community-modal-title"
                    className="text-primary fw-bold"
                >
                    {titleText}
                </Modal.Title>
            </Modal.Header>

            {submittedRequestName ? (
                <>
                    <Modal.Body className="pt-3">
                        <Alert variant="success" className="mb-0">
                            <Alert.Heading className="h5 mb-2">
                                Thank you for requesting {submittedRequestName}.
                            </Alert.Heading>
                            <p className="mb-0">
                                You will be notified when a decision is made.
                            </p>
                        </Alert>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button
                            variant="primary"
                            onClick={handleClose}
                            className="px-4 rounded-pill"
                        >
                            Close
                        </Button>
                    </Modal.Footer>
                </>
            ) : (
                <Form onSubmit={handleSubmit}>
                    <Modal.Body className="pt-3">
                        {errors.submit && (
                            <Alert variant="danger" className="mb-3">
                                {errors.submit}
                            </Alert>
                        )}

                        {/* Community Name */}
                        <Form.Group className="mb-3" controlId="community-name">
                            <Form.Label className="text-primary fw-medium">
                                Community Name{' '}
                                <span className="text-danger">*</span>
                            </Form.Label>
                            <InfoTooltip text="Up to 100 characters, and must include at least one letter or number. Names that differ only in capitalization or punctuation count as the same name." />
                            <Form.Control
                                type="text"
                                placeholder="Enter community name..."
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    if (errors.name) {
                                        setErrors({ ...errors, name: null });
                                    }
                                }}
                                isInvalid={!!errors.name}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.name}
                            </Form.Control.Feedback>
                            {name.trim() && generateSlug(name) && (
                                <Form.Text className="text-muted">
                                    <span>
                                        URL: /communities/{generateSlug(name)}
                                    </span>
                                    <InfoTooltip text="This is the web address for your community, built from the name you enter. It stays the same even if you rename the community later." />
                                </Form.Text>
                            )}
                        </Form.Group>

                        {/* Description */}
                        <Form.Group
                            className="mb-3"
                            controlId="community-description"
                        >
                            <Form.Label className="text-primary fw-medium">
                                Description{' '}
                                <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Describe your community..."
                                value={description}
                                onChange={(e) => {
                                    setDescription(e.target.value);
                                    if (errors.description) {
                                        setErrors({
                                            ...errors,
                                            description: null,
                                        });
                                    }
                                }}
                                isInvalid={!!errors.description}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.description}
                            </Form.Control.Feedback>
                            <Form.Text className="text-muted">
                                {description.length}/2000 characters
                            </Form.Text>
                        </Form.Group>

                        {/* Category */}
                        <Form.Group
                            className="mb-3"
                            controlId="community-category"
                        >
                            <Form.Label className="text-primary fw-medium">
                                Category <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Select
                                value={category}
                                onChange={(e) => {
                                    setCategory(e.target.value);
                                    if (errors.category) {
                                        setErrors({
                                            ...errors,
                                            category: null,
                                        });
                                    }
                                }}
                                isInvalid={!!errors.category}
                            >
                                <option value="">Select a category...</option>
                                {CATEGORIES.map((cat) => (
                                    <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                                {errors.category}
                            </Form.Control.Feedback>
                        </Form.Group>

                        {/* Hero Image */}
                        <Form.Group
                            className="mb-3"
                            controlId="community-hero-image"
                        >
                            <Form.Label className="text-primary fw-medium">
                                Hero Image (optional)
                            </Form.Label>
                            <div className="create-community-image-upload">
                                {heroImage ? (
                                    <div className="image-preview-container position-relative">
                                        <Image
                                            src={heroImage}
                                            alt="Hero Preview"
                                            fluid
                                            className="image-preview hero-image-preview rounded"
                                        />
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            className="position-absolute top-0 end-0 m-2 rounded-circle"
                                            onClick={() =>
                                                handleRemoveImage('hero')
                                            }
                                            aria-label="Remove hero image"
                                        >
                                            <i className="bi bi-x" />
                                        </Button>
                                    </div>
                                ) : (
                                    <div
                                        className="image-upload-zone d-flex flex-column align-items-center justify-content-center p-3 border rounded"
                                        onClick={() =>
                                            heroInputRef.current?.click()
                                        }
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                heroInputRef.current?.click();
                                            }
                                        }}
                                    >
                                        <i className="bi bi-image fs-3 text-muted mb-1" />
                                        <small className="text-muted">
                                            Click to upload hero image
                                            (JPEG/PNG/HEIC, max 5MB)
                                        </small>
                                    </div>
                                )}
                                <Form.Control
                                    type="file"
                                    ref={heroInputRef}
                                    accept={ACCEPTED_FILE_EXTENSIONS}
                                    onChange={(e) =>
                                        handleImageSelect(e, 'hero')
                                    }
                                    className="d-none"
                                />
                                {errors.hero && (
                                    <div className="text-danger small mt-2">
                                        {errors.hero}
                                    </div>
                                )}
                            </div>
                        </Form.Group>

                        {/* Icon Image */}
                        <Form.Group
                            className="mb-3"
                            controlId="community-icon-image"
                        >
                            <Form.Label className="text-primary fw-medium">
                                Icon Image (optional)
                            </Form.Label>
                            <div className="create-community-image-upload">
                                {iconImage ? (
                                    <div className="image-preview-container position-relative d-inline-block">
                                        <Image
                                            src={iconImage}
                                            alt="Icon Preview"
                                            roundedCircle
                                            className="image-preview icon-image-preview"
                                        />
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            className="position-absolute top-0 end-0 rounded-circle icon-remove-btn"
                                            onClick={() =>
                                                handleRemoveImage('icon')
                                            }
                                            aria-label="Remove icon image"
                                        >
                                            <i className="bi bi-x" />
                                        </Button>
                                    </div>
                                ) : (
                                    <div
                                        className="image-upload-zone icon-upload-zone d-flex flex-column align-items-center justify-content-center p-3 border rounded"
                                        onClick={() =>
                                            iconInputRef.current?.click()
                                        }
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (
                                                e.key === 'Enter' ||
                                                e.key === ' '
                                            ) {
                                                iconInputRef.current?.click();
                                            }
                                        }}
                                    >
                                        <i className="bi bi-person-circle fs-3 text-muted mb-1" />
                                        <small className="text-muted">
                                            Click to upload icon (JPEG/PNG/HEIC,
                                            max 5MB)
                                        </small>
                                    </div>
                                )}
                                <Form.Control
                                    type="file"
                                    ref={iconInputRef}
                                    accept={ACCEPTED_FILE_EXTENSIONS}
                                    onChange={(e) =>
                                        handleImageSelect(e, 'icon')
                                    }
                                    className="d-none"
                                />
                                {errors.icon && (
                                    <div className="text-danger small mt-2">
                                        {errors.icon}
                                    </div>
                                )}
                            </div>
                        </Form.Group>
                    </Modal.Body>

                    <Modal.Footer className="border-0 pt-0">
                        <Button
                            variant="outline-primary"
                            onClick={handleClose}
                            className="px-4 rounded-pill"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 rounded-pill"
                        >
                            {isSubmitting ? submitBusyText : submitIdleText}
                        </Button>
                    </Modal.Footer>
                </Form>
            )}
        </Modal>
    );
};

CreateCommunityModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
};

export default CreateCommunityModal;
